import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    CircularProgress,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Chip,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { semesterAPI, academicYearAPI, classAPI, semesterMappingAPI, cceConfigAPI } from '../services/api';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    academicYearId: Yup.string().required('Academic Year is required'),
    startDate: Yup.date().required('Start Date is required'),
    endDate: Yup.date().required('End Date is required')
        .min(Yup.ref('startDate'), 'End date must be after start date')
});

// ===== SEMESTER MANAGEMENT COMPONENT =====
const SemesterManagement = () => {
    const [semesters, setSemesters] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const fetchSemesters = async () => {
        try {
            setLoading(true);
            const res = await semesterAPI.getAll();
            setSemesters(res.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch semesters');
        } finally {
            setLoading(false);
        }
    };

    const fetchAcademicYears = async () => {
        try {
            const res = await academicYearAPI.getAll({ isActive: true });
            setAcademicYears(res.data.data || []);
        } catch (error) {
            console.error('Failed to fetch academic years');
        }
    };

    useEffect(() => {
        fetchSemesters();
        fetchAcademicYears();
    }, []);

    const formik = useFormik({
        initialValues: {
            name: '',
            academicYearId: '',
            startDate: '',
            endDate: '',
            description: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                if (editingId) {
                    await semesterAPI.update(editingId, values);
                    toast.success('Semester updated successfully');
                } else {
                    await semesterAPI.create(values);
                    toast.success('Semester created successfully');
                }
                setOpenDialog(false);
                fetchSemesters();
                resetForm();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Operation failed');
            }
        },
    });

    const resetForm = () => {
        formik.resetForm();
        setEditingId(null);
    };

    const handleEdit = (semester) => {
        setEditingId(semester.id);
        formik.setValues({
            name: semester.name,
            academicYearId: semester.academicYearId,
            startDate: semester.startDate,
            endDate: semester.endDate,
            description: semester.description || ''
        });
        setOpenDialog(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this semester?')) {
            try {
                await semesterAPI.delete(id);
                toast.success('Semester deleted successfully');
                fetchSemesters();
            } catch (error) {
                toast.error('Failed to delete semester');
            }
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Semester / Term Management</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => { resetForm(); setOpenDialog(true); }}
                >
                    Add Semester
                </Button>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Academic Year</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {semesters.map((semester) => (
                                <TableRow key={semester.id}>
                                    <TableCell>{semester.name}</TableCell>
                                    <TableCell>{semester.academicYear?.name}</TableCell>
                                    <TableCell>{semester.startDate}</TableCell>
                                    <TableCell>{semester.endDate}</TableCell>
                                    <TableCell>{semester.isActive ? 'Active' : 'Inactive'}</TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleEdit(semester)} size="small">
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDelete(semester.id)} size="small" color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {semesters.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">No semesters found</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? 'Edit Semester' : 'Add Semester'}</DialogTitle>
                <form onSubmit={formik.handleSubmit}>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    name="academicYearId"
                                    label="Academic Year"
                                    select
                                    value={formik.values.academicYearId}
                                    onChange={formik.handleChange}
                                    error={formik.touched.academicYearId && Boolean(formik.errors.academicYearId)}
                                    helperText={formik.touched.academicYearId && formik.errors.academicYearId}
                                >
                                    {academicYears.map((year) => (
                                        <MenuItem key={year.id} value={year.id}>{year.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    name="name"
                                    label="Semester Name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                    placeholder="e.g. Unit Test 1, Term 1, Semester 1"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    name="startDate"
                                    label="Start Date"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={formik.values.startDate}
                                    onChange={formik.handleChange}
                                    error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                                    helperText={formik.touched.startDate && formik.errors.startDate}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    name="endDate"
                                    label="End Date"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={formik.values.endDate}
                                    onChange={formik.handleChange}
                                    error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                                    helperText={formik.touched.endDate && formik.errors.endDate}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    name="description"
                                    label="Description (Optional)"
                                    multiline
                                    rows={2}
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                        <Button type="submit" variant="contained">Save</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

// ===== SEMESTER CLASS MAPPING COMPONENT =====
const SemesterClassMapping = () => {
    const [semesters, setSemesters] = useState([]);
    const [classes, setClasses] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedSemester, setSelectedSemester] = useState('');
    const [selectedClasses, setSelectedClasses] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedAcademicYear, setSelectedAcademicYear] = useState('');

    const fetchData = async () => {
        try {
            setLoading(true);
            const [semRes, classRes, mapRes, yearRes] = await Promise.all([
                semesterAPI.getAll(),
                classAPI.getAll(),
                semesterMappingAPI.getAll(),
                academicYearAPI.getAll({ isActive: true })
            ]);
            setSemesters(semRes.data.data || []);
            setClasses(classRes.data.data || []);
            setMappings(mapRes.data.data || []);
            setAcademicYears(yearRes.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSaveMapping = async () => {
        if (!selectedSemester || selectedClasses.length === 0) {
            toast.warning('Please select a semester and at least one class');
            return;
        }

        try {
            await semesterMappingAPI.create({
                semesterId: selectedSemester,
                classIds: selectedClasses,
                academicYearId: selectedAcademicYear
            });
            toast.success('Classes mapped to semester successfully');
            fetchData();
            setSelectedClasses([]);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save mapping');
        }
    };

    const handleDeleteMapping = async (id) => {
        if (window.confirm('Remove this mapping?')) {
            try {
                await semesterMappingAPI.delete(id);
                toast.success('Mapping removed');
                fetchData();
            } catch (error) {
                toast.error('Failed to remove mapping');
            }
        }
    };

    const handleClassToggle = (classId) => {
        setSelectedClasses(prev =>
            prev.includes(classId)
                ? prev.filter(id => id !== classId)
                : [...prev, classId]
        );
    };

    // Filter semesters by selected academic year
    const filteredSemesters = selectedAcademicYear
        ? semesters.filter(s => s.academicYearId === parseInt(selectedAcademicYear))
        : semesters;

    return (
        <Box>
            <Typography variant="h6" gutterBottom>Map Semesters to Classes</Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom>Create New Mapping</Typography>
                            <Divider sx={{ mb: 2 }} />

                            <TextField
                                fullWidth
                                select
                                label="Academic Year"
                                value={selectedAcademicYear}
                                onChange={(e) => {
                                    setSelectedAcademicYear(e.target.value);
                                    setSelectedSemester('');
                                }}
                                sx={{ mb: 2 }}
                            >
                                {academicYears.map(year => (
                                    <MenuItem key={year.id} value={year.id}>{year.name}</MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                fullWidth
                                select
                                label="Select Semester"
                                value={selectedSemester}
                                onChange={(e) => setSelectedSemester(e.target.value)}
                                sx={{ mb: 2 }}
                                disabled={!selectedAcademicYear}
                            >
                                {filteredSemesters.map(sem => (
                                    <MenuItem key={sem.id} value={sem.id}>{sem.name}</MenuItem>
                                ))}
                            </TextField>

                            <Typography variant="body2" sx={{ mb: 1 }}>Select Classes:</Typography>
                            <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                                {classes.map(cls => (
                                    <FormControlLabel
                                        key={cls.id}
                                        control={
                                            <Checkbox
                                                checked={selectedClasses.includes(cls.id)}
                                                onChange={() => handleClassToggle(cls.id)}
                                            />
                                        }
                                        label={cls.name}
                                        sx={{ display: 'block' }}
                                    />
                                ))}
                            </Box>

                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveMapping}
                                sx={{ mt: 2 }}
                                fullWidth
                                disabled={!selectedSemester || selectedClasses.length === 0}
                            >
                                Save Mapping
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom>Existing Mappings</Typography>
                            <Divider sx={{ mb: 2 }} />

                            {loading ? <CircularProgress size={24} /> : (
                                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                    {mappings.length === 0 ? (
                                        <Typography color="textSecondary">No mappings found</Typography>
                                    ) : (
                                        mappings.map(map => (
                                            <Box
                                                key={map.id}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    p: 1,
                                                    borderBottom: '1px solid #eee'
                                                }}
                                            >
                                                <Box>
                                                    <Chip
                                                        label={map.semester?.name}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ mr: 1 }}
                                                    />
                                                    <Chip
                                                        label={map.class?.name}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteMapping(map.id)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

// ===== CCE CONFIGURATION COMPONENT =====
const CCEConfiguration = () => {
    const [configs, setConfigs] = useState([]);
    const [classes, setClasses] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        classId: '',
        academicYearId: '',
        scholasticWeight: 80,
        coScholasticWeight: 20,
        gradingScale: [
            { grade: 'A1', minPercent: 91, maxPercent: 100, gradePoint: 10 },
            { grade: 'A2', minPercent: 81, maxPercent: 90, gradePoint: 9 },
            { grade: 'B1', minPercent: 71, maxPercent: 80, gradePoint: 8 },
            { grade: 'B2', minPercent: 61, maxPercent: 70, gradePoint: 7 },
            { grade: 'C1', minPercent: 51, maxPercent: 60, gradePoint: 6 },
            { grade: 'C2', minPercent: 41, maxPercent: 50, gradePoint: 5 },
            { grade: 'D', minPercent: 33, maxPercent: 40, gradePoint: 4 },
            { grade: 'E', minPercent: 0, maxPercent: 32, gradePoint: 0 }
        ]
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [configRes, classRes, yearRes] = await Promise.all([
                cceConfigAPI.getAll(),
                classAPI.getAll(),
                academicYearAPI.getAll({ isActive: true })
            ]);
            setConfigs(configRes.data.data || []);
            setClasses(classRes.data.data || []);
            setAcademicYears(yearRes.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSubmit = async () => {
        if (!formData.classId || !formData.academicYearId) {
            toast.warning('Please select class and academic year');
            return;
        }

        try {
            if (editingId) {
                await cceConfigAPI.update(editingId, formData);
                toast.success('CCE Configuration updated');
            } else {
                await cceConfigAPI.create(formData);
                toast.success('CCE Configuration created');
            }
            setOpenDialog(false);
            fetchData();
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const resetForm = () => {
        setFormData({
            classId: '',
            academicYearId: '',
            scholasticWeight: 80,
            coScholasticWeight: 20,
            gradingScale: [
                { grade: 'A1', minPercent: 91, maxPercent: 100, gradePoint: 10 },
                { grade: 'A2', minPercent: 81, maxPercent: 90, gradePoint: 9 },
                { grade: 'B1', minPercent: 71, maxPercent: 80, gradePoint: 8 },
                { grade: 'B2', minPercent: 61, maxPercent: 70, gradePoint: 7 },
                { grade: 'C1', minPercent: 51, maxPercent: 60, gradePoint: 6 },
                { grade: 'C2', minPercent: 41, maxPercent: 50, gradePoint: 5 },
                { grade: 'D', minPercent: 33, maxPercent: 40, gradePoint: 4 },
                { grade: 'E', minPercent: 0, maxPercent: 32, gradePoint: 0 }
            ]
        });
        setEditingId(null);
    };

    const handleEdit = (config) => {
        setEditingId(config.id);
        setFormData({
            classId: config.classId,
            academicYearId: config.academicYearId,
            scholasticWeight: config.scholasticWeight,
            coScholasticWeight: config.coScholasticWeight,
            gradingScale: config.gradingScale
        });
        setOpenDialog(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Delete this configuration?')) {
            try {
                await cceConfigAPI.delete(id);
                toast.success('Configuration deleted');
                fetchData();
            } catch (error) {
                toast.error('Failed to delete');
            }
        }
    };

    const updateGradeScale = (index, field, value) => {
        const newScale = [...formData.gradingScale];
        newScale[index][field] = field.includes('Percent') || field === 'gradePoint' ? Number(value) : value;
        setFormData({ ...formData, gradingScale: newScale });
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">CCE Exam Configuration</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => { resetForm(); setOpenDialog(true); }}
                >
                    Add Configuration
                </Button>
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Class</TableCell>
                                <TableCell>Academic Year</TableCell>
                                <TableCell>Scholastic Weight</TableCell>
                                <TableCell>Co-Scholastic Weight</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {configs.map(config => (
                                <TableRow key={config.id}>
                                    <TableCell>{config.class?.name}</TableCell>
                                    <TableCell>{config.academicYear?.name}</TableCell>
                                    <TableCell>{config.scholasticWeight}%</TableCell>
                                    <TableCell>{config.coScholasticWeight}%</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={config.isActive ? 'Active' : 'Inactive'}
                                            color={config.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleEdit(config)} size="small">
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDelete(config.id)} size="small" color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {configs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">No configurations found</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editingId ? 'Edit CCE Configuration' : 'Add CCE Configuration'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                select
                                label="Class"
                                value={formData.classId}
                                onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
                            >
                                {classes.map(cls => (
                                    <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                select
                                label="Academic Year"
                                value={formData.academicYearId}
                                onChange={(e) => setFormData({ ...formData, academicYearId: e.target.value })}
                            >
                                {academicYears.map(year => (
                                    <MenuItem key={year.id} value={year.id}>{year.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Scholastic Weight (%)"
                                value={formData.scholasticWeight}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    scholasticWeight: Number(e.target.value),
                                    coScholasticWeight: 100 - Number(e.target.value)
                                })}
                                inputProps={{ min: 0, max: 100 }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Co-Scholastic Weight (%)"
                                value={formData.coScholasticWeight}
                                disabled
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Grading Scale</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Grade</TableCell>
                                            <TableCell>Min %</TableCell>
                                            <TableCell>Max %</TableCell>
                                            <TableCell>Grade Point</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {formData.gradingScale.map((grade, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        value={grade.grade}
                                                        onChange={(e) => updateGradeScale(idx, 'grade', e.target.value)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={grade.minPercent}
                                                        onChange={(e) => updateGradeScale(idx, 'minPercent', e.target.value)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={grade.maxPercent}
                                                        onChange={(e) => updateGradeScale(idx, 'maxPercent', e.target.value)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={grade.gradePoint}
                                                        onChange={(e) => updateGradeScale(idx, 'gradePoint', e.target.value)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// ===== MAIN EXAM SETUP COMPONENT =====
const ExamSetup = () => {
    const [value, setValue] = useState(0);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
                Exam & Result Setup
            </Typography>
            <Paper sx={{ width: '100%' }}>
                <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto">
                    <Tab label="Add Semester / Term" />
                    <Tab label="Semester Map To Class" />
                    <Tab label="Marksheet Policy" disabled />
                    <Tab label="CCE Exam Configuration" />
                </Tabs>
                <Box sx={{ p: 3 }}>
                    {value === 0 && <SemesterManagement />}
                    {value === 1 && <SemesterClassMapping />}
                    {value === 2 && <Typography>Marksheet Policy (Coming Soon)</Typography>}
                    {value === 3 && <CCEConfiguration />}
                </Box>
            </Paper>
        </Box>
    );
};

export default ExamSetup;
